import debug from 'debug';

import middleware from './middleware';
import { handleAnonId } from '../storage';
import { validScriptIntegrations } from '../tracking/integrations';
import { getTrackingService } from '../tracking/TrackingServiceInstance';

import type { IntegrationData } from '$lib/components/ConsentOverlay/types';

const debugLog = debug('global:analytics');

let loaded = false;

export const loadAnalytics = ({
    companyId,
    callback,
    preventTracking,
    analyticsLink,
}: {
    companyId: string;
    callback: () => void;
    preventTracking: boolean;
    analyticsLink: string;
}) => {
    debugLog('-> loadAnalytics', companyId, preventTracking, analyticsLink);

    if (preventTracking) {
        callback();

        return;
    }

    window.analytics.load = () => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = analyticsLink;
        script.onerror = function () {
            if (getTrackingService().isV4()) {
                window.sendV4ScriptBlockEvent?.();
            }
        };

        const headElement = document.getElementsByTagName('head')[0];
        headElement?.appendChild(script);

        script.onload = function () {
            callback();
        };
    };
    window.analytics.SNIPPET_VERSION = '4.1.0';
    window.analytics.load();
};

export const loadedIntegrations: Record<string, boolean> = {};

export const activateIntegrations = (integrations: IntegrationData) => {
    const integrationNames = Object.keys(integrations);
    const notLoaded: IntegrationData = {};

    for (let i = 0; i < integrationNames.length; i++) {
        const integrationName = integrationNames[i];

        if (!loadedIntegrations[integrationName]) {
            notLoaded[integrationName] = integrations[integrationName];
        }
    }

    if (!Object.keys(notLoaded).length) {
        return;
    }

    if (!loaded || !window.analytics?.initialize) {
        // when preferences are set in the localstorage, this method is called immediately
        // even when analytics.js is not loaded yet - so we add a event listener
        // instead of calling the `initializeWithIntegrations` directly
        window.analytics.ready(() => {
            initializeWithIntegrations(integrations);
        });
    } else {
        initializeWithIntegrations(integrations);
    }
};

const initializeWithIntegrations = (integrations: IntegrationData) => {
    // adds all given integrations to analytics.js and loads them
    window.analytics.initialize(integrations);
    Object.keys(integrations).forEach((integrationName) => {
        loadedIntegrations[integrationName] = true;
    });

    const ps_queue = (window as any).ps_queue || [];
    while (ps_queue.length > 0) {
        const argumentList = ps_queue.shift();
        // can be identify, track, page
        const method = argumentList.shift();

        if (window.analytics[method]) {
            let contextIndex = -1;

            if (method === 'identify' || method === 'track') {
                contextIndex = 2;
            }

            if (method === 'page') {
                contextIndex = 3;
            }

            // only process known methods
            if (contextIndex > -1) {
                // deactivate perspective tracking for all events because
                // perspective tracking was already initialized and triggered
                argumentList[contextIndex].integrations = {
                    ...(argumentList[contextIndex].integrations || {}),
                    Perspective: false,
                };
                // this will do something like
                // window.analytics.track('event', {params}, { integrations: { Perspective: false }});
                // window.analytics.identify('anon_id', {traits}, { integrations: { Perspective: false }});
                // window.analytics.page(null, 'home', {params}, { integrations: { Perspective: false }});
                window.analytics[method](...argumentList);
            }
        }
    }
};

export const loadAnalyticsWrapper = async ({
    companyId,
    integrations,
    preventTracking,
    analyticsLink,
}: {
    companyId: string;
    integrations: Record<string, boolean>;
    preventTracking: boolean;
    analyticsLink: string;
}) => {
    if (loaded) {
        return;
    }
    loaded = true;

    const duplicate = JSON.parse(JSON.stringify(integrations));

    Object.keys(duplicate).forEach((displayName) => {
        if (validScriptIntegrations.indexOf(displayName) === -1) {
            delete duplicate[displayName];
        }

        loadedIntegrations[displayName] = true;
    });

    // Await PS_TRACKING before loading the analytics.js script to prevent a
    // race condition: if the script loads first, analytics.ready() callbacks
    // (queued by cookie consent) can call analytics.initialize() before
    // initCallback adds middleware, causing "attempted to add an integration
    // middleware after initialization".
    try {
        const { default: PS_TRACKING } = await import('$lib/global/core/ps-integration');
        window.PS_TRACKING = PS_TRACKING;
    } catch (err) {
        console.warn('Failed to load PS_TRACKING integration:', err);
    }

    const initCallback = () => {
        // CON-3317 / CON-3856: Scope user traits to the current session to prevent
        // cross-campaign data leakage. Set before initialize() so defaults are merged
        // into the User singleton's _options.
        updateSegmentTraitsKey(getTrackingService().sessionId);

        handleAnonId();

        // removes call by reference bindings
        if (window.analytics && window.analytics.initialize) {
            try {
                if (getTrackingService().isV4()) {
                    window.analytics.use(window.PS_TRACKING);
                }
                window.analytics.addIntegrationMiddleware(middleware);
                window.analytics.initialize(duplicate);
            } catch (err) {
                console.warn('analytics init error', err);
                throw err;
            }

            // Loop through the interim analytics queue and reapply the calls to their
            // proper analytics.js method.
            while (window.analytics_queue.length > 0) {
                const item = window.analytics_queue.shift();
                const method = item.shift();

                if (window.analytics[method]) {
                    window.analytics[method](...item);
                }
            }
        }
    };

    loadAnalytics({
        companyId,
        preventTracking,
        analyticsLink,
        callback: () => {
            initCallback();
        },
    });
};

export default loadAnalyticsWrapper;

/**
 * Update Segment's user traits storage key to be session-scoped.
 *
 * Called at two points:
 * 1. Before analytics.initialize() — sets User.defaults so initialize merges it into _options.
 * 2. After session regeneration — updates _options directly on the existing singleton.
 *
 * CON-3317: Prevents cross-campaign data leakage on the same domain.
 * CON-3856: Keeps traits scoped to the current session.
 */
export const updateSegmentTraitsKey = (sessionId: string): void => {
    const key = `${sessionId}_user_traits`;

    if (!window.analytics?.user) return;

    const user = window.analytics.user();

    // After analytics.reset(), the User constructor reference may be cleared.
    // Guard against undefined to prevent "Cannot read properties of undefined (reading 'defaults')".
    // Related support case: https://linear.app/perspective/issue/FUN-3240/bug-preview-is-not-working
    if (user.User?.defaults?.localStorage) {
        user.User.defaults.localStorage.key = key;
    }

    if (user._options?.localStorage) {
        user._options.localStorage.key = key;
    }
};
